import { onBeforeUnmount, readonly, ref, watch, type Ref } from 'vue'
import { createCarousel, type Carousel, type CarouselOptions } from '../utils/carousel'

/**
 * utils/carousel for a Vue component: wires the carousel to `root` whenever it holds an
 * element (inside <ClientOnly> that is after the first mount) and mirrors its index.
 * Markup and CSS contract: see utils/carousel.ts and assets/styles/carousel.css.
 */
export function useCarousel(
  root: Ref<HTMLElement | null | undefined>,
  options: CarouselOptions = {},
) {
  const index = ref(options.index ?? 0)
  const maxIndex = ref(0)
  let carousel: Carousel | undefined

  const stop = () => {
    carousel?.destroy()
    carousel = undefined
  }

  watch(
    root,
    el => {
      stop()
      if (!el) return
      carousel = createCarousel(el, {
        ...options,
        onChange: change => {
          index.value = change.index
          maxIndex.value = change.maxIndex
          options.onChange?.(change)
        },
      })
    },
    { flush: 'post', immediate: true },
  )
  onBeforeUnmount(stop)

  return {
    index: readonly(index),
    maxIndex: readonly(maxIndex),
    next: (speed?: number) => carousel?.next(speed),
    prev: (speed?: number) => carousel?.prev(speed),
    slideTo: (target: number, speed?: number) => carousel?.slideTo(target, speed),
  }
}
